<template>
  <div>
    <main class="shipment-notice__main">
      <v-form
        ref="form"
        class="create-add-form"
        @submit.prevent
      >
        <v-row>
          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.shipfrom') }}
              </label>
              <v-autocomplete
                v-model="asn.supplier"
                :loading="loadingSuppliers"
                :items="suppliers"
                :disabled="!business || !editor"
                :search-input.sync="supplierSearch"
                item-text="asdb"
                item-value="asdb"
                :readonly="!business"
                :clearable="business"
                :placeholder="$t('common.type_to_search')"
                hide-no-data
                solo
                :rules="[rules.required]"
                @change="supplierChangeEvent"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.shipto') }}
              </label>
              <v-combobox
                v-model="asn.plant"
                :loading="loadingSuppliers"
                :items="plants"
                item-text="plant"
                item-value="plant.plant"
                :return-object="false"
                :placeholder="$t('common.type_to_search')"
                hide-no-data
                solo
                :rules="[rules.required]"
                @change="onPlantChange"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('common.seller') }}
              </label>
              <v-text-field
                v-model="asn.seller"
                :readonly="!business || !editor"
                solo
                :rules="[rules.required]"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('common.buyer') }}
              </label>
              <v-text-field
                v-model="asn.buyer"
                readonly
                solo
                :rules="[rules.required]"
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            class="pb-0"
            cols="3"
          >
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.carrier') }}
              </label>
              <v-combobox
                v-model="asn.carrier"
                :items="carriers"
                :placeholder="$t('common.type_to_search')"
                hide-no-data
                solo
              />
            </div>
          </v-col>

          <v-col
            class="pb-0"
            cols="3"
          >
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.transportation_mode') }}
              </label>
              <v-select
                v-model="asn.modeOfTransportation"
                :items="transportationModes"
                solo
              />
            </div>
          </v-col>

          <v-col cols="3">
            <div class="input-block input-block_white align-center">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.purchase_accountable') }}
              </label>
              <v-checkbox
                v-model="asn.purchasingThroughAccountablePerson"
                class="mt-0"
                hide-details
              />
            </div>
          </v-col>

          <v-col
            class="pb-0"
            cols="3"
          >
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.accountable_person') }}
              </label>
              <v-text-field
                v-model="asn.accountablePerson"
                :disabled="!asn.purchasingThroughAccountablePerson"
                solo
                :rules="[rules.requiredPerson]"
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.lotNumber') }}
              </label>
              <v-text-field
                v-model="asn.lotNumber"
                solo
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.container_number') }}
              </label>
              <v-text-field
                v-model="asn.containerNumber"
                solo
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.shipping_date') }}
              </label>
              <date-picker
                v-model="asn.shippingDate"
                required
                :is-edit="!!initialAsn"
                @input="clearContract"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.shipping_time') }}
              </label>
              <time-picker
                v-model="asn.shippingTime"
                required
                :is-edit="!!initialAsn"
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.bill_of_lading') }}
              </label>
              <v-text-field
                v-model="asn.billOfLading"
                solo
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.freight_bill_number') }}
              </label>
              <v-text-field
                v-model="asn.freightBillNumber"
                solo
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.net_weight') }}
              </label>
              <v-text-field
                v-model="asn.netWeightKg"
                type="number"
                solo
                :rules="[rules.optionalPositiveNumber]"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.gross_weight') }}
              </label>
              <v-text-field
                v-model="asn.grossWeightKg"
                type="number"
                hint=""
                solo
                :rules="[rules.optionalPositiveNumber]"
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.invoice_date') }}
              </label>
              <date-picker
                v-model="asn.invoiceDate"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.invoice_number') }}
              </label>
              <v-text-field
                v-model="asn.invoiceNumber"
                solo
              />
            </div>
          </v-col>

          <v-col
            v-if="!asn.import"
            class="pb-0"
          >
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.torg_12_date') }}
              </label>
              <date-picker
                v-model="asn.torg12Date"
              />
            </div>
          </v-col>

          <v-col
            v-if="!asn.import"
            class="pb-0"
          >
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_creation.torg_12_number') }}
              </label>
              <v-text-field
                v-model="asn.torg12Number"
                solo
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-if="previousSupplier && previousSupplier.manualContract"
            class="pb-0"
          >
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_details.contract') }}
              </label>
              <v-autocomplete
                v-model="asn.contract"
                :loading="loadingContracts"
                :items="contracts"
                :disabled="!business || !editor"
                :search-input.sync="contractSearch"
                item-text="name"
                return-object
                :readonly="!business || !editor"
                :clearable="business && editor"
                :placeholder="$t('common.type_to_search')"
                no-filter
                hide-no-data
                solo
                @change="onContractChange"
              />
            </div>
          </v-col>

          <v-col class="pb-0">
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_details.shipping_name') }}
              </label>
              <v-text-field
                v-model="asn.shippingName"
                :readonly="!business || !editor"
                solo
              />
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <div class="input-block input-block_white">
              <label class="input-block__label">
                {{ $t('views.shipment_notice_list.comment') }}
              </label>
              <v-textarea
                v-model="asn.comment"
                solo
                rows="2"
                auto-grow
              />
            </div>
          </v-col>
        </v-row>
      </v-form>

      <p>{{ $t('views.shipment_notice_creation.footnote') }}</p>
    </main>

    <shipment-notice-parts
      :parts="parts"
      :validation-error="partsValidationError"
      :buyer="asn.buyer"
      :seller="asn.seller"
      :shipping-date="utcShippingDate()"
      :manual-contract="Boolean(previousSupplier) && previousSupplier.manualContract"
      :contract="asn.contract"
      @addPart="addPart"
      @updatePart="updatePart"
      @removePart="removePart"
    />

    <shipment-notice-packing
      :packing="packing"
      :parts="parts"
      :validation-error="packingValidationError"
      :buyer="asn.buyer"
      :seller="asn.seller"
      :shipping-date="utcShippingDate()"
      :manual-contract="Boolean(previousSupplier) && previousSupplier.manualContract"
      :contract="asn.contract"
      @addPacking="addPacking"
      @updatePacking="updatePacking"
      @removePacking="removePacking"
    />

    <div class="mt-7">
      <button
        :disabled="saving"
        class="btn spso-button spso-button_line spso-button_lowercase"
        @click="delayedSave"
      >
        {{ isNew ? $t('common.create') : $t('common.save') }}
      </button>
    </div>

    <clearing-modal
      v-model="isClearingModalShown"
      @restorePrevious="restorePreviousPlant"
      @clearTables="clearTablesAndContract"
    />

    <clearing-modal
      v-model="isContractChanged"
      @restorePrevious="restorePreviousContract"
      @clearTables="clearTables"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import exactMath from 'exact-math';
import { transportationModes, carriers } from '@/utils/enums';
import ShipmentNoticeParts from '@/components/asn/ShipmentNoticeParts.vue';
import ShipmentNoticePacking from '@/components/asn/ShipmentNoticePacking.vue';
import ClearingModal from '@/components/asn/ClearingModal.vue';
import DatePicker from '@/components/common/DatePicker.vue';
import TimePicker from '@/components/common/TimePicker.vue';
import { mongoRegexI } from '@/utils/mongo-regex';

export default {
  name: 'ShipmentNoticeForm',

  components: {
    ShipmentNoticeParts,
    ShipmentNoticePacking,
    ClearingModal,
    DatePicker,
    TimePicker,
  },

  props: {
    initialAsn: {
      type: Object,
      default: () => null,
    },

    isNew: {
      type: Boolean,
      default: () => false,
    },

    saving: {
      type: Boolean,
      default: () => false,
    },
  },

  data() {
    return {
      asn: {
        supplier: '',
        carrier: '',
        plant: '',
        seller: '',
        buyer: '',
        comment: '',
        lotNumber: '',
        import: null,
        containerNumber: '',
        shippingDate: this.$moment().format(this.$config.dateFormat),
        shippingTime: '00:00',
        modeOfTransportation: '',
        billOfLading: '',
        freightBillNumber: '',
        netWeightKg: undefined,
        grossWeightKg: undefined,
        accountablePerson: '',
        purchasingThroughAccountablePerson: false,
        invoiceDate: '',
        invoiceNumber: '',
        torg12Date: '',
        torg12Number: '',
        details: [],
        packing: [],
        contract: { guid: '', name: '' },
        shippingName: '',
      },

      parts: [],
      packing: [],

      transportationModes,
      carriers,

      suppliers: [],
      loadingSuppliers: false,
      supplierSearch: '',
      isSupplierDisabled: false,
      plants: [],
      loadingPlants: false,
      plantsSearch: '',
      contracts: [],
      loadingContracts: false,
      contractSearch: '',

      previousPlant: '',
      previousSupplier: {},
      previousContract: {},
      isClearingModalShown: false,
      isContractChanged: false,

      partsValidationError: '',
      packingValidationError: '',

      rules: {
        required: (value) => Boolean(value) || this.$t('validation.required'),
        requiredPerson: (value) => this.asn.purchasingThroughAccountablePerson && (Boolean(value) || this.$t('validation.required')),
        optionalPositiveNumber: (value) => {
          if (typeof value === 'undefined') { return true; }

          // eslint-disable-next-line no-restricted-globals
          if (!isNaN(value) && value > 0) { return true; }

          return this.$t('validation.positive_number');
        },
      },
    };
  },

  computed: {
    ...mapState({ user: 'user', supplierData: 'supplier' }),
    ...mapGetters(['business', 'editor', 'supplier']),

    order() {
      return this.$route.query.order;
    },
  },

  watch: {
    supplierSearch(value) {
      if (value) {
        this.getSuppliers(value);
      }
    },

    contractSearch(value) {
      this.getContracts(value);
    },
  },

  async created() {
    await this.initFields();
    this.initAsn();
  },

  methods: {
    onPlantChange(val) {
      this.asn.plant = val;
      this.previousPlant = this.asn.plant;
    },

    supplierChangeEvent() {
      if (this.previousSupplier && (this.asn.supplier === this.previousSupplier.asdb)) {
        return;
      }

      this.setPlantsBySupplier(this.asn.supplier);

      if (!this.parts.length && !this.packing.length) {
        this.previousSupplier = this.getCurrentSupplier(this.asn.supplier);
        this.previousPlant = this.asn.plant;
        return;
      }

      this.isClearingModalShown = true;
    },

    onContractChange(val) {
      if (!val) {
        this.clearContract();
      }
      if (this.previousContract.guid === this.asn.contract.guid) {
        return;
      }
      if (!this.parts.length && !this.packing.length) {
        this.previousContract = this.asn.contract;
        return;
      }
      this.isContractChanged = true;
    },

    async initFields() {
      if (this.initialAsn) {
        this.asn = { ...this.asn, ...this.initialAsn };

        const {
          supplier, plant, seller, buyer, shippingDate, invoiceRu, TORG12,
        } = this.initialAsn;

        this.asn.supplier = (supplier && supplier.asdb) || supplier;
        this.asn.plant = (plant && plant.asdb) || plant;
        if (this.asn.plant) {
          this.plants = [this.asn.plant];
        }
        this.asn.seller = (seller && seller.asdb) || seller;
        this.asn.buyer = (buyer && buyer.asdb) || buyer;

        this.asn.shippingDate = this.getDate(shippingDate, this.$config.dateFormat);
        this.asn.shippingTime = shippingDate ? this.getDate(shippingDate, 'HH:mm') : '00:00';
        if (invoiceRu) {
          this.asn.invoiceDate = this.getDate(invoiceRu.date, this.$config.dateFormat, 'Europe/Moscow');
          this.asn.invoiceNumber = invoiceRu.number;
        }

        if (TORG12) {
          this.asn.torg12Date = this.getDate(TORG12.date, this.$config.dateFormat, 'Europe/Moscow');
          this.asn.torg12Number = TORG12.number;
        }
      } else {
        this.asn.import = Boolean(this.supplierData && this.supplierData.isLocal);
      }

      if (!this.business) {
        this.isSupplierDisabled = true;

        if (!this.initialAsn) {
          this.asn.supplier = this.user.asdb;
        }
      }

      if (this.asn.supplier) {
        await this.getSuppliers(this.asn.supplier);
        if (this.suppliers && this.suppliers.length > 0) {
          const supplier = this.getCurrentSupplier(this.asn.supplier);

          if (supplier && supplier.plants) {
            this.plants = supplier.plants;
          }

          // asn.import could be null or undefined. In both cases we will set it from supplier
          if (supplier && this.asn.import == null) {
            this.asn.import = !supplier.isLocal;
          }
        }
      }

      if (this.asn.contract) {
        this.contracts = [this.asn.contract];
      }
    },

    initAsn() {
      const {
        details,
        packing,
      } = this.asn;

      this.previousSupplier = this.getCurrentSupplier(this.asn.supplier);
      this.previousPlant = this.asn.plant;
      this.previousContract = this.asn.contract;

      if (!this.initialAsn) {
        this.asn.buyer = this.$config.defaultBuyer;
      }

      if (!this.initialAsn || this.initialAsn.isAfterSales) {
        // Try to define plant, seller, buyer based on supplier and defaults
        this.setPlantsBySupplier(this.asn.supplier, this.asn.plant);
      }

      this.parts = details;
      this.packing = packing;
    },

    async getSuppliers(str) {
      this.loadingSuppliers = true;

      try {
        if (this.business) {
          const query = {
            asdb: mongoRegexI(str),
          };

          const { data = {} } = await this.$http.get('/suppliers', { params: { query } });
          this.suppliers = data.rows;
        } else {
          //const { data = {} } = await this.$http.get(`/suppliers-get-by-asdb/?asdb=${encodeURIComponent(this.user.asdb)}`);
          const url = `/suppliers/${encodeURIComponent(this.user.asdb)}`
          const { data = {} } = await this.$http.get(url);
          this.suppliers = [data];
        }
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingSuppliers = false;
      }
    },

    async getContracts(str) {
      this.loadingContracts = true;

      try {
        const shippingDate = this.$moment(this.asn.shippingDate || undefined).startOf('day').toISOString();
        const query = {
          name: mongoRegexI(str),
          supplier: this.asn.seller,
          archived: false,
          startDate: { $lte: shippingDate },
          endDate: { $gte: shippingDate },
        };
        const { data = {} } = await this.$http.get('/contracts', { params: { query } });
        this.contracts = data.rows;
      } catch (err) {
        this.$http.mockApiError(err);
      } finally {
        this.loadingContracts = false;
      }
    },

    getDate(date, format = 'LLL', tz) {
      if (!date) { return ''; }

      const mDate = tz ? this.$moment.tz(date, tz) : this.$moment(date);

      return ((new Date(date)).toISOString() !== '1970-01-01T00:00:00.000Z')
        ? mDate.format(format) : '';
    },

    addPart(item) {
      this.parts.push({ ...item });
      this.resetPartsAndPackingValidation();
    },

    updatePart(index, item) {
      this.parts.splice(index, 1, { ...item });
      this.resetPartsAndPackingValidation();
    },

    removePart(index) {
      this.parts.splice(index, 1);
      this.resetPartsAndPackingValidation();
    },

    addPacking(item) {
      this.packing.push({ ...item });
      this.resetPartsAndPackingValidation();
    },

    updatePacking(index, item) {
      this.packing.splice(index, 1, { ...item });
      this.resetPartsAndPackingValidation();
    },

    removePacking(index) {
      this.packing.splice(index, 1);
      this.resetPartsAndPackingValidation();
    },

    restorePreviousPlant() {
      this.suppliers = [this.previousSupplier];
      this.asn.supplier = this.previousSupplier.asdb;

      this.setPlantsBySupplier(this.previousSupplier.asdb, this.previousPlant);
    },

    restorePreviousContract() {
      this.contracts = [this.previousContract];
      this.asn.contract = this.previousContract;
    },

    clearTablesAndContract() {
      this.parts = [];
      this.packing = [];

      this.previousSupplier = this.getCurrentSupplier(this.asn.supplier);
      this.previousPlant = this.asn.plant;
      this.clearContract();
    },

    clearContract() {
      this.asn.contract = { guid: '', name: '' };
    },

    clearTables() {
      this.parts = [];
      this.packing = [];
      this.previousContract = this.asn.contract;
    },

    // Need to wait before all blur events happend, and onChange called
    delayedSave() {
      const self = this;
      setTimeout(() => self.save(), 100);
    },

    save() {
      if (!this.validate()) {
        this.$toast(this.$t('views.shipment_notice_creation.check_field_errors'), {
          title: this.$t('toast.danger'),
          variant: 'danger',
        });
        return;
      }

      const {
        supplier,
        shippingDate,
        shippingTime,
        invoiceDate,
        invoiceNumber,
        torg12Date,
        torg12Number,
        accountablePerson,
        ...header
      } = this.asn;

      const { asdb } = this.supplierData || {};

      const asn = {
        ...header,
        accountablePerson: header.purchasingThroughAccountablePerson ? accountablePerson : '',
        import: this.asn.import,
        shippingDate: this.utcShippingDate(),
        supplier: this.business && this.editor ? supplier : asdb,
        source: 'SP',
        details: this.parts,
        packing: this.packing,
      };

      if (invoiceNumber) {
        asn.invoiceRu = {
          date: invoiceDate && this.$moment.tz(invoiceDate, 'Europe/Moscow').startOf('day').toISOString(),
          number: invoiceNumber,
        };
      }
      if (!asn.import) {
        asn.TORG12 = {
          date: torg12Date && this.$moment.tz(torg12Date, 'Europe/Moscow').startOf('day').toISOString(),
          number: torg12Number,
        };
      }

      this.$emit('save', asn);
    },

    validate() {
      this.$refs.form.validate();
      const required = [
        'supplier',
        'plant',
        'buyer',
        'seller',
        'shippingDate',
      ];

      if (this.asn.purchasingThroughAccountablePerson) {
        required.push('accountablePerson');
      }

      this.validatePartsAndPacking();
      return required.every((key) => this.asn[key])
        && !this.partsValidationError
        && !this.packingValidationError;
    },

    validatePartsAndPacking() {
      this.resetPartsAndPackingValidation();

      if (!this.parts.length) {
        this.partsValidationError = this.$t('validation.table_empty');
      }
      if (!this.packing.length) {
        this.packingValidationError = this.$t('validation.table_empty');
      }
      if (this.partsValidationError || this.packingValidationError) {
        return;
      }

      const partsSums = this.parts.reduce((acc, position) => {
        acc[position.detailNumber] = exactMath.add(
          acc[position.detailNumber] || 0, position.quantitySent,
        );
        return acc;
      }, {});
      const packingSums = this.packing.reduce((acc, position) => {
        acc[position.detailNumber] = exactMath.add(
          acc[position.detailNumber] || 0, position.quantity,
        );
        return acc;
      }, {});

      const partsMissing = Object.entries((packingSums)).reduce((acc, [pn, qty]) => {
        const partQty = partsSums[pn] || 0;
        if (partQty < qty) {
          acc.push(`${this.$t('validation.parts_missing')} ${pn}: ${partQty} < ${qty}`);
        }
        return acc;
      }, []);
      const packingMissing = Object.entries((partsSums)).reduce((acc, [pn, qty]) => {
        const packingQty = packingSums[pn] || 0;
        if (packingQty < qty) {
          acc.push(`${this.$t('validation.packing_missing')} ${pn}: ${qty} > ${packingQty}`);
        }
        return acc;
      }, []);

      this.packingValidationError = [...partsMissing, ...packingMissing].join('<br/>');
    },

    resetPartsAndPackingValidation() {
      this.partsValidationError = '';
      this.packingValidationError = '';
    },

    getCurrentSupplier(asdb) {
      return this.suppliers.find((item) => item.asdb === asdb);
    },

    setPlantsBySupplier(val, plant) {
      const supplier = this.getCurrentSupplier(val);

      this.asn.plant = '';

      if (!supplier) {
        this.plants = [];
        this.asn.seller = val;

        return;
      }

      this.asn.import = !supplier.isLocal;
      this.plants = supplier.plants;
      this.asn.seller = supplier.seller || supplier.asdb;

      if (plant) {
        this.asn.plant = plant;

        return;
      }

      if (this.plants.length === 1) {
        this.asn.plant = this.plants[0].plant;
      }
    },

    utcShippingDate() {
      const [shippingHour, shippingMinute] = this.asn.shippingTime.split(':');

      if (!(this.asn.shippingDate && this.asn.shippingTime && shippingHour && shippingMinute)) { return ''; }

      let res;
      try {
        res = this.$moment(this.asn.shippingDate)
          .hour(shippingHour)
          .minute(shippingMinute)
          .toISOString();
      } catch (e) {
        res = '';
      }

      return res;
    },
  },
};
</script>

<style lang="scss" scoped>
.shipment-notice__main {
  padding: 25px;
  background-color: #fff;
}
</style>

<style lang="scss">
.shipment-notice__main .v-input__slot {
  box-shadow: none !important;
}
</style>
